import { watch, type WatchStopHandle } from 'vue'

export type MetaEntryOld = {
  isInitial: boolean
  isPending: boolean
  hasError: boolean
}

export type MetaOld<T extends string> = Record<T, MetaEntryOld>

export function createMetaOld<T extends string>(keys: readonly T[]) {
  return keys.reduce((acc, key) => {
    acc[key] = getMetaInitialOld()

    return acc
  }, {} as MetaOld<T>)
}

export function getMetaInitialOld(): MetaEntryOld {
  return {
    isInitial: true,
    isPending: false,
    hasError: false,
  }
}

export function getMetaPendingOld(): MetaEntryOld {
  return {
    isInitial: false,
    isPending: true,
    hasError: false,
  }
}

export function getMetaSuccessOld(): MetaEntryOld {
  return {
    isInitial: false,
    isPending: false,
    hasError: false,
  }
}

export function getMetaErrorOld(): MetaEntryOld {
  return {
    isInitial: false,
    isPending: false,
    hasError: true,
  }
}

let requestCount = 0

export function getNewRequestId() {
  return requestCount++
}

export type MetaEntry<T = any> =
  | {
      status: 'initial'
      requestId?: undefined
      params?: undefined
      error?: undefined
    }
  | {
      status: 'pending'
      requestId: number
      params: T
      error?: undefined
    }
  | {
      status: 'success'
      requestId?: undefined
      params: T
      error?: undefined
    }
  | {
      status: 'error'
      requestId?: undefined
      params: T
      error: any
    }

export type Meta<T extends Record<string, any>> = {
  [K in keyof T]: MetaEntry<T[K]>
}

export function createMeta<T extends Record<string, any>>(
  keys: (keyof T)[]
): T {
  return keys.reduce((acc, key) => {
    acc[key] = getMetaInitial() as any

    return acc
  }, {} as T)
}

export function getMetaInitial(): MetaEntry<any> {
  return {
    status: 'initial',
  }
}

export function getMetaPending<T>(requestId: number, params: T): MetaEntry<T> {
  return {
    status: 'pending',
    requestId,
    params,
  }
}

export function getMetaSuccess<T>(params: T): MetaEntry<T> {
  return {
    status: 'success',
    params,
  }
}

export function getMetaError<T>(params: T, error: any): MetaEntry<T> {
  return {
    status: 'error',
    params,
    error,
  }
}

export function waitFor(check: () => boolean) {
  if (check()) {
    return
  }

  return new Promise<void>((resolve) => {
    let unwatch: WatchStopHandle | undefined = undefined

    unwatch = watch(check, () => {
      if (check()) {
        unwatch?.()

        resolve()
      }
    })
  })
}

type Where<T, P> = T extends P ? T : never

export async function callWithMeta<
  T extends
    | { status: 'success'; payload: any }
    | { status: 'error'; payload: any },
  S extends { meta: Meta<Record<string, any>> },
  M extends Partial<{ [K in keyof S['meta']]: S['meta'][K]['params'] }>,
>({
  store,
  metaFields,
  apiCall,
  onSuccess,
  onError,
}: {
  store: S
  metaFields: M
  apiCall: (params: { isLatest: (metaKey: keyof M) => boolean }) => Promise<T>
  onSuccess?: (params: {
    isLatest: (metaKey: keyof M) => boolean
    result: Where<T, { status: 'success' }>
  }) => Promise<void> | void
  onError?: (params: {
    isLatest: (metaKey: keyof M) => boolean
    result: Where<T, { status: 'error' }>
  }) => Promise<void> | void
}) {
  // Initialize meta pending states

  const requestId = getNewRequestId()

  Object.entries(metaFields).forEach(([metaKey, params]) => {
    store.meta[metaKey] = getMetaPending(requestId, params)
  })

  const isLatest = (metaKey: keyof M) =>
    store.meta[metaKey].requestId === requestId

  // Perform request

  const result = await apiCall({ isLatest })

  // Process and store response

  if (result.status === 'success') {
    await onSuccess?.({
      isLatest,
      result: result as Where<T, { status: 'success' }>,
    })
  } else {
    await onError?.({
      isLatest,
      result: result as Where<T, { status: 'error' }>,
    })
  }

  // Update meta state

  Object.entries(metaFields).forEach(([metaKey, params]) => {
    if (isLatest(metaKey)) {
      if (result.status === 'success') {
        store.meta[metaKey] = getMetaSuccess(params)
      } else {
        store.meta[metaKey] = getMetaError(params, result.payload)
      }
    }
  })

  return {
    isLatest,
    result,
  }
}
